import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { IState } from '../../../shared/models/state';
import { getAreNumbersLoadedSelector, getNumbersSelector } from '../pricing-numbers.reducer';
import { Observable } from 'rxjs';
import { PhoneNumber } from '../pricing-numbers';
import { debounceTime, distinctUntilChanged, startWith } from 'rxjs/operators';
import { AccountService } from '../../../shared/services/account.service';
import { Location } from '@angular/common';
import { ClearNumbersLoadedStateAction, GetNumbersAction } from '../pricing-numbers.action';
import { AmplitudeService } from '../../../shared/services/amplitude.service';

@Component({
  selector: 'cc-choose-number',
  templateUrl: './choose-number.component.html',
  styleUrls: ['./choose-number.component.scss']
})
export class ChooseNumberComponent implements OnInit, OnDestroy {
  public filtersForm: FormGroup;
  public pricingId: number;
  public phoneNumbers$: Observable<PhoneNumber[]>;
  public arePhoneNumbersLoaded$: Observable<boolean>;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private store: Store<IState>,
    private router: Router,
    private accountService: AccountService,
    private location: Location,
    private amplitudeService: AmplitudeService
  ) {}

  ngOnInit() {
    this.filtersForm = this.fb.group({
      search: ['']
    });

    this.pricingId = this.route.snapshot.params['pricingId'];
    this.phoneNumbers$ = this.store.select(getNumbersSelector);
    this.arePhoneNumbersLoaded$ = this.store.select(getAreNumbersLoadedSelector);

    this.filtersForm.controls['search'].valueChanges
      .pipe(startWith(''), debounceTime(300), distinctUntilChanged())
      .subscribe((searchText: string) => {
        this.store.dispatch(new GetNumbersAction({ pricingId: this.pricingId, searchText }));
      });

    this.logAmplitude('Init');
  }

  ngOnDestroy() {
    this.store.dispatch(new ClearNumbersLoadedStateAction());
  }

  refreshNumbers() {
    const searchText = this.filtersForm.controls['search'].value;
    this.store.dispatch(new GetNumbersAction({ pricingId: this.pricingId, searchText }));
    this.logAmplitude('Refresh numbers');
  }

  noNumbersForPricing(numbers: PhoneNumber[]) {
    return !this.numbersForPricingExists(numbers);
  }

  numbersForPricingExists(numbers: PhoneNumber[]) {
    return numbers.length || this.userFilteredNumbers();
  }

  chooseNumber(number: PhoneNumber) {
    this.logAmplitude('Number chosen', number);
    this.accountService.getAccountConfig().subscribe(conf => {
      const isFullyConfigured = conf.data;
      const baseUrl = isFullyConfigured ? 'get-new-number' : 'get-first-new-number';
      this.router.navigate([baseUrl, 'pricing', this.pricingId, 'final-step'], {
        queryParams: {
          selectedNumber: number.number,
          selectedMsisdnId: number.id
        }
      });
    });
  }

  back() {
    this.logAmplitude('Going back');
    this.location.back();
  }

  private userFilteredNumbers() {
    return this.filtersForm.get('search').value !== '';
  }

  private logAmplitude(event: string, properties: any = {}) {
    this.amplitudeService.logEvent(`Panel:Numbers:Choose number table: ${event}`, {
      pricingId: this.pricingId,
      ...properties
    });
  }
}
